import { useLazyQuery } from '@apollo/client';
import {
  EmploymentStatus,
  MaritalStatus,
  RelatedEntityTypes,
  type TaxIdTypes,
} from '@generated/graphql';
import type { CustomField } from '@interfaces/customField';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import type { AreaOfStudy, JobTitle, Profession, User } from '@interfaces/user';
import {
  eligibleTaxIdTypes,
  getAreasOfStudy,
  getJobTitles,
  getProfessions,
  taxIdTypeFormatType,
} from '@interfaces/user';
import { MenuItem } from '@mui/material';
import { AddressField } from '@ovComponents/2-component/addressField/addressField';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { MaskedTaxIdField } from '@ovComponents/3-pattern/maskedTaxIdField/maskedTaxIdField';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { PhoneNumberField } from '@ovComponents/3-pattern/phoneNumberField/phoneNumberField';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { formatPhoneNumber, phoneValidator } from '@utils/common';
import { dollars2cents } from '@utils/dollars2cents';
import dayjs from 'dayjs';
import { Box, Button, Grid, Typography } from 'hephaestus/web';
import type { CountryCode } from 'libphonenumber-js';
import { kebabCase, omit } from 'lodash-es';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { VALIDATE_TAX_ID } from '../residencyInformation/residencyInformation.visual';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';

type SelectOption = {
  label?: string;
  value?: string;
};

const cents2dollars = (value?: number) =>
  value === undefined || value === null ? '' : value / 100;

export const RelationshipInformationVisual = ({
  options = {},
  userData,
  loading,
  updateUser,
  updating,
  continueFunc,
  workflowCompletion,
  grid,
  updateMode,
  activeCustomFields,
  derivedCustomFields,
  ignoreCustomFieldTriggerRules,
  relationOptions = [],
  onSelectRelationEntity,
  onClearRelationEntity,
  selectedSpouseEntityId = '',
  selectedTrustedContactEntityId = '',
  resolvedUser,
}: {
  options: any;
  userData: any;
  loading: boolean;
  updateUser: any;
  updating: boolean;
  continueFunc: any;
  grid?: boolean;
  updateMode?: boolean;
  workflowCompletion?: any;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  ignoreCustomFieldTriggerRules?: boolean;
  relationOptions?: any[];
  onSelectRelationEntity?: (
    entityId: string,
    relation: RelatedEntityTypes,
  ) => Promise<void> | void;
  onClearRelationEntity?: (
    relation: RelatedEntityTypes,
  ) => Promise<void> | void;
  selectedSpouseEntityId?: string;
  selectedTrustedContactEntityId?: string;
  resolvedUser?: any;
}) => {
  const { translateBackendString } = useLocalization();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation('client');
  // Phone values are stored as digits only (no "+"), so validation needs a
  // country to resolve the region. The per-field `countryCode` state is only
  // populated when the user edits through the country-aware input, so fall back
  // to the organization's default phone country to avoid flagging valid numbers.
  const defaultPhoneCountryCode = (
    activeOrganization?.applicableLocalization?.phoneNumberCountryCodes?.[0] ??
    activeOrganization?.applicableLocalization?.countries?.[0]
  )?.toUpperCase() as CountryCode | undefined;
  const [focused, setFocused] = useState<string[]>([]);
  const [updated, setUpdated] = useState<boolean>(false);
  const [invalidFieldSpouseTaxId, setInvalidFieldSpouseTaxId] =
    useState<boolean>(false);
  const [invalidFieldsList, setInvalidFieldsList] = useState<string[]>([]);
  const [invalidPhone, setInvalidPhone] = useState<Record<string, boolean>>({
    spouseEmployerCompanyPhone: false,
    trustedContactPhone: false,
  });
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: userData.customFields,
    });
  const [countryCode, setCountryCode] = useState<
    Record<string, CountryCode | undefined>
  >({
    spouseEmployerCompanyPhone: undefined,
    trustedContactPhone: undefined,
  });

  const requireSpousalInfo = (data: Partial<User>): boolean =>
    [
      MaritalStatus.Married,
      MaritalStatus.CommonLaw,
      MaritalStatus.CivilUnion,
    ].includes(data?.maritalStatus as MaritalStatus);

  const requireSpousalEmploymentInfo = (data: Partial<User>): boolean =>
    [
      EmploymentStatus.Retired,
      EmploymentStatus.Employed,
      EmploymentStatus.SelfEmployed,
      EmploymentStatus.Unemployed,
    ].includes(data?.spouseEmploymentStatus as EmploymentStatus);

  const [selectedTcpId, setSelectedTcpId] = useState('');
  const [selectedSpouseId, setSelectedSpouseId] = useState('');
  // Remember the last saved-entity id we synced into the dropdowns. The backend
  // selection prop lags behind a manual Clear (the refetched relatedEntities can
  // still contain the just-removed person for a moment), so without this guard
  // the sync effect below would re-apply the value the user just cleared -
  // forcing a second click. Recording the id on clear prevents that re-apply.
  const lastSyncedSpouseIdRef = useRef<string>('');
  const lastSyncedTcpIdRef = useRef<string>('');

  // Reflect any already-saved spouse / trusted-contact related entity in the
  // dropdowns on load (and whenever the fetched selection changes), so the
  // fields render as locked/auto-filled. Only sync when the saved entity is
  // actually present in relationOptions: the select/clear UI only renders when
  // there are options, so without a matching option the fields would lock with
  // no way to unlock or clear them.
  useEffect(() => {
    if (
      selectedSpouseEntityId &&
      selectedSpouseEntityId !== lastSyncedSpouseIdRef.current &&
      relationOptions.some((r: any) => r?.id === selectedSpouseEntityId)
    ) {
      lastSyncedSpouseIdRef.current = selectedSpouseEntityId;
      setSelectedSpouseId(selectedSpouseEntityId);
    }
  }, [selectedSpouseEntityId, relationOptions]);

  useEffect(() => {
    if (
      selectedTrustedContactEntityId &&
      selectedTrustedContactEntityId !== lastSyncedTcpIdRef.current &&
      relationOptions.some((r: any) => r?.id === selectedTrustedContactEntityId)
    ) {
      lastSyncedTcpIdRef.current = selectedTrustedContactEntityId;
      setSelectedTcpId(selectedTrustedContactEntityId);
    }
  }, [selectedTrustedContactEntityId, relationOptions]);

  const anyTrustedContactFieldEnabled = [
    'trustedContactName',
    'trustedContactEmail',
    'trustedContactPhone',
    'trustedContactAddress',
  ].some((key) => options?.[key]?.enabled);

  const anySpouseFieldEnabled = [
    'spouseFirstName',
    'spouseMiddleName',
    'spouseLastName',
    'spouseEmail',
    'spouseTaxId',
    'spouseDateOfBirth',
    'spouseEmploymentStatus',
    'spouseEmployerName',
    'spouseCompanyType',
    'spouseJobTitle',
    'spouseAreaOfStudy',
    'spouseEmployerCompanyAddress',
    'spouseEmployerCompanyPhone',
    'spouseAnnualIncome',
  ].some((key) => options?.[key]?.enabled);

  // Auto-fill the trusted-contact fields from a selected household member,
  // only for the fields that are enabled in this widget.
  const applyTrustedContactFromUser = (relationUser: any) => {
    update({
      ...userData,
      trustedContactPerson: {
        ...userData.trustedContactPerson,
        ...(options?.trustedContactName?.enabled && {
          name: [relationUser.firstName, relationUser.lastName]
            .filter(Boolean)
            .join(' '),
        }),
        ...(options?.trustedContactEmail?.enabled && {
          email: relationUser.primaryEmail ?? '',
        }),
        ...(options?.trustedContactPhone?.enabled && {
          phone: relationUser.phone
            ? formatPhoneNumber(relationUser.phone)
            : '',
        }),
        ...(options?.trustedContactAddress?.enabled && {
          physicalAddress: relationUser.physicalAddress ?? {},
        }),
      },
    });
  };

  // Auto-fill the spouse fields from a selected household member, only for the
  // fields that are enabled in this widget.
  const applySpouseFromUser = (relationUser: any) => {
    update({
      ...userData,
      ...(options?.spouseFirstName?.enabled && {
        spouseFirstName: relationUser.firstName ?? '',
      }),
      ...(options?.spouseMiddleName?.enabled && {
        spouseMiddleName: relationUser.middleName ?? '',
      }),
      ...(options?.spouseLastName?.enabled && {
        spouseLastName: relationUser.lastName ?? '',
      }),
      ...(options?.spouseEmail?.enabled && {
        spouseEmail: relationUser.primaryEmail ?? '',
      }),
      ...(options?.spouseTaxId?.enabled && {
        // A user's SIN is stored as `taxId` on the backend.
        spouseTaxId: relationUser.taxId ?? '',
        spouseTaxIdType: relationUser.taxIdType ?? userData.spouseTaxIdType,
        spouseTaxIdDisclosed: relationUser.taxIdDisclosed ?? undefined,
      }),
      ...(options?.spouseDateOfBirth?.enabled && {
        spouseDateOfBirth: relationUser.dateOfBirth ?? null,
      }),
      ...(options?.spouseEmploymentStatus?.enabled && {
        spouseEmploymentStatus: relationUser.employmentStatus ?? undefined,
      }),
      ...(options?.spouseEmployerName?.enabled && {
        spouseEmployerName: relationUser.employerName ?? '',
      }),
      ...(options?.spouseCompanyType?.enabled && {
        spouseCompanyType: relationUser.companyType ?? '',
      }),
      ...(options?.spouseJobTitle?.enabled && {
        spouseJobTitle: relationUser.jobTitle ?? '',
      }),
      ...(options?.spouseAreaOfStudy?.enabled && {
        spouseAreaOfStudy: relationUser.studentAreaOfStudy ?? '',
      }),
      ...(options?.spouseEmployerCompanyAddress?.enabled && {
        spouseEmployerCompanyAddress: relationUser.employerCompanyAddress ?? {},
      }),
      ...(options?.spouseAnnualIncome?.enabled && {
        spouseAnnualIncomeCents: relationUser.annualIncomeCents ?? undefined,
        spouseAnnualIncomeStr: relationUser.annualIncomeStr ?? undefined,
      }),
    });
  };

  // Dropdown label for a household member: "First Last (email)", falling back
  // to just the email when no name is available.
  const relationOptionLabel = (relationUser: any): string => {
    const name = [relationUser?.firstName, relationUser?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return name
      ? `${name} (${relationUser?.primaryEmail ?? ''})`
      : (relationUser?.primaryEmail ?? '');
  };

  // While a household member is selected, the auto-filled fields are locked
  // (greyed out). Clearing the selection lifts the lock and resets the values.
  const spouseAutoFilled = !!selectedSpouseId;
  const trustedContactAutoFilled = !!selectedTcpId;

  // A field only carries a value when the fetch API actually returned data for
  // it. Fields with no fetched value (e.g. SIN, or an empty employment status /
  // annual income) must stay editable, otherwise required fields would be
  // greyed out and empty, blocking the user from submitting.
  //
  // Locks are derived from the server-resolved user (`resolvedUser`) rather than
  // the live `userData`. That data only changes when the selection is saved and
  // refetched, so a field the selected spouse / trusted contact resolved empty
  // stays editable while the user types into it (typing does not re-lock it),
  // and it reflects the *newly* selected person rather than any previous value.
  const hasFetchedValue = (value: any): boolean => {
    if (value === undefined || value === null || value === '') return false;
    if (typeof value === 'object') {
      // Ignore GraphQL metadata (e.g. `__typename`) and treat objects whose
      // fields are all empty/null (such as an address shell returned by the
      // API) as having no usable value, so unfilled required inputs stay
      // editable instead of being locked and empty.
      return Object.entries(value).some(
        ([key, nestedValue]) =>
          key !== '__typename' && hasFetchedValue(nestedValue),
      );
    }
    return true;
  };
  // A phone value made up of only a dial code (e.g. "+1") is effectively empty
  // and must stay editable so the user can fill it in.
  const hasFetchedPhoneValue = (value?: string): boolean => {
    if (!value) return false;
    return value.replace(/\D/g, '').length > 4;
  };
  const spouseFieldLocked = (key: string): boolean =>
    !!selectedSpouseId && hasFetchedValue(resolvedUser?.[key]);
  const spousePhoneLocked = (key: string): boolean =>
    !!selectedSpouseId && hasFetchedPhoneValue(resolvedUser?.[key]);
  // A tax id that comes back as only asterisks (e.g. "*********" / "*** *** ***")
  // is a masked placeholder, not a usable value: it cannot be submitted as a
  // real SIN and fails validation ("SIN required"). Treat masked-only tax ids as
  // "not fetched" so the field stays editable and the user can enter a real SIN.
  const isMaskedTaxId = (value?: string | null): boolean =>
    !!value && /^[*\s]+$/.test(value);
  const spouseTaxIdLocked = (): boolean =>
    !!selectedSpouseId &&
    hasFetchedValue(resolvedUser?.spouseTaxId) &&
    !isMaskedTaxId(resolvedUser?.spouseTaxId);
  const trustedContactFieldLocked = (key: string): boolean =>
    !!selectedTcpId &&
    hasFetchedValue(resolvedUser?.trustedContactPerson?.[key]);
  // Phone fields must use the dial-code-aware check so a trusted-contact phone
  // that resolved to only a dial code stays editable (mirrors spousePhoneLocked).
  const trustedContactPhoneLocked = (key: string): boolean =>
    !!selectedTcpId &&
    hasFetchedPhoneValue(resolvedUser?.trustedContactPerson?.[key]);

  const clearSelectedSpouse = async () => {
    const previousSpouseId = selectedSpouseId;
    const previousUserData = userData;
    // Record the id being cleared so the sync effect doesn't re-apply it while
    // the backend selection prop still lags behind this clear.
    lastSyncedSpouseIdRef.current = selectedSpouseEntityId || previousSpouseId;
    setSelectedSpouseId('');
    applySpouseFromUser({});
    try {
      await onClearRelationEntity?.(RelatedEntityTypes.Spouse);
    } catch {
      lastSyncedSpouseIdRef.current = '';
      setSelectedSpouseId(previousSpouseId);
      update(previousUserData);
    }
  };

  const clearSelectedTrustedContact = async () => {
    const previousTcpId = selectedTcpId;
    const previousUserData = userData;
    // Record the id being cleared so the sync effect doesn't re-apply it while
    // the backend selection prop still lags behind this clear.
    lastSyncedTcpIdRef.current =
      selectedTrustedContactEntityId || previousTcpId;
    setSelectedTcpId('');
    applyTrustedContactFromUser({});
    try {
      await onClearRelationEntity?.(RelatedEntityTypes.TrustedContact);
    } catch {
      lastSyncedTcpIdRef.current = '';
      setSelectedTcpId(previousTcpId);
      update(previousUserData);
    }
  };

  const validate = () => {
    const fields = invalidFields(
      {
        ...omit(options, ['spouseAnnualIncome']),
        spouseFirstName: {
          ...options.spouseFirstName,
          requiredIf: requireSpousalInfo,
        },
        spouseMiddleName: {
          ...options.spouseMiddleName,
          requiredIf: requireSpousalInfo,
        },
        spouseLastName: {
          ...options.spouseLastName,
          requiredIf: requireSpousalInfo,
        },
        spouseEmail: { ...options.spouseEmail, requiredIf: requireSpousalInfo },
        spouseTaxId: {
          ...options?.spouseTaxId,
          requiredIf: () => requireSpousalInfo(userData),
        },
        spouseSin: { enabled: false },
        spouseEmploymentStatus: {
          ...options.spouseEmploymentStatus,
          requiredIf: requireSpousalInfo,
        },
        spouseEmployerName: {
          ...options.spouseEmployerName,
          requiredIf: () =>
            requireSpousalInfo(userData) &&
            userData.spouseEmploymentStatus === 'EMPLOYED',
        },
        spouseEmployerCompanyAddress: {
          ...options.spouseEmployerCompanyAddress,
          requiredIf: () =>
            requireSpousalInfo(userData) &&
            userData.spouseEmploymentStatus === 'EMPLOYED',
        },
        spouseEmployerCompanyPhone: {
          ...options.spouseEmployerCompanyPhone,
          requiredIf: () =>
            requireSpousalInfo(userData) &&
            userData.spouseEmploymentStatus === 'EMPLOYED',
        },
        spouseCompanyType: {
          ...options.spouseCompanyType,
          requiredIf: () =>
            requireSpousalInfo(userData) &&
            requireSpousalEmploymentInfo(userData),
        },
        spouseJobTitle: {
          ...options.spouseJobTitle,
          requiredIf: () =>
            requireSpousalInfo(userData) &&
            userData.spouseEmploymentStatus === 'EMPLOYED',
        },
        spouseAreaOfStudy: {
          ...options.spouseAreaOfStudy,
          requiredIf: () =>
            requireSpousalInfo(userData) &&
            userData.spouseEmploymentStatus === 'STUDENT',
        },
        spouseDateOfBirth: {
          ...options.spouseDateOfBirth,
          requiredIf: requireSpousalInfo,
        },
        spouseAnnualIncomeCents: !options?.spouseAnnualIncome?.selectField
          ?.checked && {
          ...options.spouseAnnualIncome,
          requiredIf: requireSpousalInfo,
        },
        spouseAnnualIncomeStr: options?.spouseAnnualIncome?.selectField
          ?.checked && {
          ...options.spouseAnnualIncome,
          requiredIf: requireSpousalInfo,
        },
      },
      userData,
    );

    if (
      userData.trustedContactPerson?.phone &&
      !phoneValidator(
        userData.trustedContactPerson?.phone,
        countryCode.trustedContactPhone ?? defaultPhoneCountryCode,
      )
    ) {
      fields.push('trustedContactPhone');
      setInvalidPhone({ ...invalidPhone, trustedContactPhone: true });
    } else {
      setInvalidPhone({ ...invalidPhone, trustedContactPhone: false });
    }

    if (
      userData.spouseEmployerCompanyPhone &&
      !phoneValidator(
        userData.spouseEmployerCompanyPhone,
        countryCode.spouseEmployerCompanyPhone ?? defaultPhoneCountryCode,
      )
    ) {
      fields.push('spouseEmployerCompanyPhone');
      setInvalidPhone({ ...invalidPhone, spouseEmployerCompanyPhone: true });
    } else {
      setInvalidPhone({ ...invalidPhone, spouseEmployerCompanyPhone: false });
    }

    const invalidCustomFieldKeys = validateAllCustomFields();
    fields.push(...invalidCustomFieldKeys);
    setInvalidFieldsList(fields);
    setFocused(fields);
    return fields.length === 0;
  };

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  const submit = async () => {
    if (validate()) {
      if (workflowCompletion) {
        sendAnalytic(
          ovAnalyticsEvents.workflowsRelationshipInformationContinueButtonSelect,
          {
            workflowStepTitle: options?.title,
            workflowId: workflowCompletion.workflow?.id,
            workflowName: workflowCompletion.workflow?.name,
            activeWorkflowCompletionId: workflowCompletion.id,
            objectId: workflowCompletion.objectId,
            objectType: workflowCompletion.objectType,
          },
        );
      }
      await continueFunc();
    }
  };

  const [validateTaxId] = useLazyQuery(VALIDATE_TAX_ID);
  const update = (newValue: any) => {
    setUpdated(true);
    updateUser(newValue);
  };

  useEffect(() => {
    if (updating === false) setUpdated(false);
  }, [updating]);

  /* (spouse) taxId logic */
  const isIndividual = userData.type === 'INDIVIDUAL';
  const countryOfTaxResidence =
    userData?.countryOfTaxResidence ?? userData?.physicalAddress?.country;

  const taxIdTypesShown: TaxIdTypes[] = useMemo(() => {
    if (!isIndividual) return [];
    if (!countryOfTaxResidence) return [];

    return eligibleTaxIdTypes(countryOfTaxResidence, true);
  }, [isIndividual, countryOfTaxResidence]);

  const defaultTaxIdType = taxIdTypesShown[0];

  const optionSpouseTaxIdEnabled =
    (options?.spouseTaxId?.enabled || options?.spouseSin?.enabled) &&
    taxIdTypesShown?.length >= 1;

  const spouseTaxIdLabel = t('shared:somethingBelongingToSomeone', {
    whose: t('details.relationOptions.SPOUSE'),
    what: `${t(`taxId:${userData.spouseTaxIdType}`)} (${t(`taxId:abbreviated.${userData.spouseTaxIdType}`)})`,
  });

  useEffect(() => {
    if (!userData) return;
    if (!optionSpouseTaxIdEnabled) return;

    // no taxIdType selected yet - pick default
    if (!userData.spouseTaxIdType && defaultTaxIdType) {
      updateUser({ ...userData, spouseTaxIdType: defaultTaxIdType });
    }
    // stale taxIdType exists that's no longer allowed - reset to default
    if (
      userData.spouseTaxIdType &&
      !taxIdTypesShown.includes(userData.spouseTaxIdType)
    ) {
      updateUser({
        ...userData,
        spouseTaxIdType: defaultTaxIdType,
        spouseTaxId: undefined,
      });
    }
  }, [
    userData,
    defaultTaxIdType,
    updateUser,
    taxIdTypesShown,
    optionSpouseTaxIdEnabled,
  ]);

  useEffect(() => {
    if (!optionSpouseTaxIdEnabled) return;

    if (focused.includes('spouseTaxId') && userData.spouseTaxIdType) {
      validateTaxId({
        variables: {
          taxIdType: userData.spouseTaxIdType,
          taxId: userData.spouseTaxId ?? '',
        },
        onCompleted: (data) => {
          setInvalidFieldSpouseTaxId(!data.validateTaxId);
        },
      });
    }
  }, [userData, optionSpouseTaxIdEnabled, focused, validateTaxId]);

  /* (spouse) taxId logic - end */

  const lockMessage = (): string =>
    options.customLockedMessage
      ? translateBackendString(options.customLockedMessage)
      : t('pageConfiguration:notEditableMessage');

  const professions = getProfessions().map(
    (profession: Profession) => profession.title,
  );
  const jobTitles = getJobTitles({ professionTitle: userData.companyType }).map(
    (jobTitle: JobTitle) => jobTitle.title,
  );
  const areasOfStudy = getAreasOfStudy().map(
    (areaOfStudy: AreaOfStudy) => areaOfStudy.title,
  );

  const isSpouseNotActivelyEmployed = [
    EmploymentStatus.Retired,
    EmploymentStatus.Unemployed,
  ].includes(userData?.spouseEmploymentStatus as EmploymentStatus);

  const spouseCompanyTypeLabel = isSpouseNotActivelyEmployed
    ? t('previousLabel', {
        label: translateBackendString(options?.spouseCompanyType?.label),
      })
    : translateBackendString(options?.spouseCompanyType?.label);

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      showSubmitButton={!updateMode || options.displayUpdateButton}
      submitButtonProps={{
        label: t(updateMode ? 'update' : 'continue'),
        disabled:
          loading || (!updated && updateMode) || invalidFieldSpouseTaxId,
        dataTestId: `relationship-information-${kebabCase(
          translateBackendString(options?.title) ?? '',
        )}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackendString(options?.title)}
          <Box
            sx={{
              display: 'inline-flex',
            }}
          >
            {additionalInfo(
              translateBackendString,
              options?.title?.additionalInfo,
            ) && (
              <InfoDialog
                information={
                  additionalInfo(
                    translateBackendString,
                    options?.title?.additionalInfo,
                  ) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}

      {options?.subtitle && (
        <Typography
          variant='bodyLarge'
          component={'div'}
          sx={{
            mb: 3,
            table: { width: '100%' },
            display: 'flex',
            alignItems: 'end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              m: 0,
              '& > *': { m: 0 },
            }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}

      <Grid container spacing={2}>
        {options?.maritalStatus?.enabled && (
          <Grid size={12}>
            <SelectField
              testId='marital-status'
              onChange={(e: any) =>
                update({ ...userData, maritalStatus: e.target.value })
              }
              disabled={loading}
              label={translateBackendString(options?.maritalStatus?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.maritalStatus?.additionalInfo,
              )}
              fullWidth
              locked={
                options?.maritalStatus?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              value={userData.maritalStatus ?? ''}
              onBlur={() => setFocused([...focused, 'maritalStatus'])}
              error={
                ((!userData.maritalStatus &&
                  focused.includes('maritalStatus')) ||
                  invalidFieldsList.includes('maritalStatus')) &&
                focused.includes('maritalStatus') &&
                options?.maritalStatus?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            >
              <MenuItem data-testid='marital-status-single' value='SINGLE'>
                {t('edit.maritalStatusOptions.SINGLE')}
              </MenuItem>
              <MenuItem data-testid='marital-status-married' value='MARRIED'>
                {t('edit.maritalStatusOptions.MARRIED')}
              </MenuItem>
              <MenuItem
                data-testid='marital-status-common-law'
                value='COMMON_LAW'
              >
                {t('edit.maritalStatusOptions.COMMON_LAW')}
              </MenuItem>
              <MenuItem data-testid='marital-status-divorced' value='DIVORCED'>
                {t('edit.maritalStatusOptions.DIVORCED')}
              </MenuItem>
              <MenuItem
                data-testid='marital-status-separated'
                value='SEPARATED'
              >
                {t('edit.maritalStatusOptions.SEPARATED')}
              </MenuItem>
              <MenuItem data-testid='marital-status-widowed' value='WIDOWED'>
                {t('edit.maritalStatusOptions.WIDOWED')}
              </MenuItem>
              <MenuItem
                data-testid='marital-status-civil-union'
                value='CIVIL_UNION'
              >
                {t('edit.maritalStatusOptions.CIVIL_UNION')}
              </MenuItem>
            </SelectField>
          </Grid>
        )}
        {anySpouseFieldEnabled &&
          requireSpousalInfo(userData) &&
          relationOptions.length > 0 && (
            <Grid size={12}>
              <Grid
                container
                spacing={2}
                sx={{
                  alignItems: 'flex-end',
                }}
              >
                <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
                  <SelectField
                    testId='spouse-select'
                    label={t('shared:relationshipInformation.selectSpouse')}
                    fullWidth
                    disabled={loading}
                    value={selectedSpouseId}
                    onChange={async (e: any) => {
                      const relationUser = relationOptions.find(
                        (r: any) => r.id === e.target.value,
                      );
                      if (!relationUser) return;
                      // Snapshot so we can roll back if persistence fails.
                      const previousSpouseId = selectedSpouseId;
                      const previousUserData = userData;
                      setSelectedSpouseId(e.target.value);
                      applySpouseFromUser(relationUser);
                      try {
                        await onSelectRelationEntity?.(
                          relationUser.id,
                          RelatedEntityTypes.Spouse,
                        );
                      } catch {
                        setSelectedSpouseId(previousSpouseId);
                        update(previousUserData);
                      }
                    }}
                  >
                    {relationOptions.map((relationUser: any) => (
                      <MenuItem
                        key={relationUser.id}
                        value={relationUser.id}
                        data-testid={`spouse-option-${relationUser.id}`}
                      >
                        {relationOptionLabel(relationUser)}
                      </MenuItem>
                    ))}
                  </SelectField>
                </Grid>
                {spouseAutoFilled && (
                  <Grid size='auto'>
                    <Button
                      variant='outlined'
                      size='small'
                      data-testid='spouse-select-clear'
                      disabled={loading}
                      onClick={clearSelectedSpouse}
                    >
                      {t('shared:relationshipInformation.clearSelectedSpouse')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        {options?.spouseFirstName?.enabled && requireSpousalInfo(userData) && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='spouse-first-name'
              onChange={(e: any) =>
                update({ ...userData, spouseFirstName: e.target.value })
              }
              disabled={loading || spouseFieldLocked('spouseFirstName')}
              label={translateBackendString(options?.spouseFirstName?.label)}
              fullWidth
              locked={
                options?.spouseFirstName?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.spouseFirstName?.additionalInfo,
              )}
              value={userData.spouseFirstName ?? ''}
              onBlur={() => setFocused([...focused, 'spouseFirstName'])}
              error={
                ((!userData.spouseFirstName &&
                  requireSpousalInfo(userData) &&
                  focused.includes('spouseFirstName')) ||
                  invalidFieldsList.includes('spouseFirstName')) &&
                focused.includes('spouseFirstName') &&
                options?.spouseFirstName?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.spouseMiddleName?.enabled && requireSpousalInfo(userData) && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='spouse-middle-name'
              onChange={(e: any) =>
                update({ ...userData, spouseMiddleName: e.target.value })
              }
              disabled={loading || spouseFieldLocked('spouseMiddleName')}
              label={translateBackendString(options?.spouseMiddleName?.label)}
              locked={
                options?.spouseMiddleName?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              fullWidth
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.spouseMiddleName?.additionalInfo,
              )}
              value={userData.spouseMiddleName ?? ''}
              onBlur={() => setFocused([...focused, 'spouseMiddleName'])}
              error={
                ((!userData.spouseMiddleName &&
                  focused.includes('spouseMiddleName')) ||
                  invalidFieldsList.includes('spouseMiddleName')) &&
                options?.spouseMiddleName?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.spouseLastName?.enabled && requireSpousalInfo(userData) && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='spouse-last-name'
              onChange={(e: any) =>
                update({ ...userData, spouseLastName: e.target.value })
              }
              disabled={loading || spouseFieldLocked('spouseLastName')}
              label={translateBackendString(options?.spouseLastName?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.spouseLastName?.additionalInfo,
              )}
              locked={
                options?.spouseLastName?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              fullWidth
              value={userData.spouseLastName ?? ''}
              onBlur={() => setFocused([...focused, 'spouseLastName'])}
              error={
                ((!userData.spouseLastName &&
                  requireSpousalInfo(userData) &&
                  focused.includes('spouseLastName')) ||
                  invalidFieldsList.includes('spouseLastName')) &&
                options?.spouseLastName?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.spouseEmail?.enabled && requireSpousalInfo(userData) && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='spouse-email'
              onChange={(e: any) =>
                update({ ...userData, spouseEmail: e.target.value })
              }
              disabled={loading || spouseFieldLocked('spouseEmail')}
              label={translateBackendString(options?.spouseEmail?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.spouseEmail?.additionalInfo,
              )}
              locked={
                options?.spouseEmail?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              fullWidth
              value={userData.spouseEmail ?? ''}
              onBlur={() => setFocused([...focused, 'spouseEmail'])}
              error={
                ((!userData.spouseEmail &&
                  requireSpousalInfo(userData) &&
                  focused.includes('spouseEmail')) ||
                  invalidFieldsList.includes('spouseEmail')) &&
                options?.spouseEmail?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {optionSpouseTaxIdEnabled && requireSpousalInfo(userData) && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <MaskedTaxIdField
              taxIdType={userData.spouseTaxIdType}
              label={spouseTaxIdLabel}
              value={userData.spouseTaxId}
              // When a spouse is picked from the dropdown the SIN must stay
              // hidden: don't expose the disclosed value (which drives the 👁️
              // reveal toggle). The user can still enter one manually and see
              // what they type; if a SIN is on file they only see the mask.
              disclosedValue={
                selectedSpouseId ? undefined : userData.spouseTaxIdDisclosed
              }
              setValue={(newTaxId: any) => {
                update({ ...userData, spouseTaxId: newTaxId });
              }}
              disabled={loading || updating || spouseTaxIdLocked()}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.spouseTaxId?.additionalInfo,
              )}
              locked={
                options?.spouseTaxId?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              fullWidth
              type={taxIdTypeFormatType(userData.spouseTaxIdType)}
              onFocus={() => setFocused([...focused, 'spouseTaxId'])}
              error={
                invalidFieldSpouseTaxId ||
                invalidFieldsList.includes('spouseTaxId')
              }
              testId='spouse-tax-id'
            />
            {taxIdTypesShown.length > 1 && (
              <Box>
                <RadioGroup
                  value={userData.spouseTaxIdType ?? ''}
                  onChange={(e: any) => {
                    update({ ...userData, spouseTaxIdType: e.target.value });
                    setFocused([...focused, 'spouseTaxId']);
                  }}
                  testId='spouse-tax-id-type'
                >
                  {taxIdTypesShown.map((type) => (
                    <>
                      <Radio
                        key={type}
                        testId={`spouse-tax-id-option-${type}`}
                        label={type}
                        value={type}
                        size='small'
                        disabled={loading || updating || spouseTaxIdLocked()}
                      />
                    </>
                  ))}
                </RadioGroup>
              </Box>
            )}
          </Grid>
        )}
        {options?.spouseDateOfBirth?.enabled &&
          requireSpousalInfo(userData) && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              <DateField
                dataTestId='spouse-date-of-birth'
                onChange={(date: any) =>
                  update({
                    ...userData,
                    spouseDateOfBirth: dayjs(date?.toString()).format(
                      'YYYY-MM-DD',
                    ),
                  })
                }
                disabled={loading || spouseFieldLocked('spouseDateOfBirth')}
                label={translateBackendString(
                  options?.spouseDateOfBirth?.label,
                )}
                infoTooltip={additionalInfo(
                  translateBackendString,
                  options?.spouseDateOfBirth?.additionalInfo,
                )}
                locked={
                  options?.spouseDateOfBirth?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                lockMessage={lockMessage()}
                fullWidth
                value={userData.spouseDateOfBirth}
                onBlur={() => setFocused([...focused, 'spouseDateOfBirth'])}
                error={
                  ((!userData.spouseDateOfBirth &&
                    requireSpousalInfo(userData) &&
                    focused.includes('spouseDateOfBirth')) ||
                    invalidFieldsList.includes('spouseDateOfBirth')) &&
                  options?.spouseDateOfBirth?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
              />
            </Grid>
          )}
        {options?.spouseEmploymentStatus?.enabled &&
          requireSpousalInfo(userData) && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              <SelectField
                testId='spouse-employment-status'
                onChange={(e: any) =>
                  update({
                    ...userData,
                    spouseEmploymentStatus: e.target.value,
                  })
                }
                disabled={
                  loading ||
                  spouseFieldLocked('spouseEmploymentStatus') ||
                  options?.spouseEmploymentStatus?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                locked={
                  options?.spouseEmploymentStatus?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                lockMessage={lockMessage()}
                label={translateBackendString(
                  options?.spouseEmploymentStatus?.label,
                )}
                infoTooltip={additionalInfo(
                  translateBackendString,
                  options?.spouseEmploymentStatus?.additionalInfo,
                )}
                fullWidth
                value={userData.spouseEmploymentStatus ?? ''}
                onBlur={() =>
                  setFocused([...focused, 'spouseEmploymentStatus'])
                }
                error={
                  !userData.spouseEmploymentStatus &&
                  focused.includes('spouseEmploymentStatus') &&
                  options?.spouseEmploymentStatus?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
              >
                <MenuItem
                  data-testid='spouse-employment-status-employed'
                  value='EMPLOYED'
                >
                  {t('edit.employmentStatusOptions.EMPLOYED')}
                </MenuItem>
                <MenuItem
                  data-testid='spouse-employment-status-self-employed'
                  value='SELF_EMPLOYED'
                >
                  {t('edit.employmentStatusOptions.SELF_EMPLOYED')}
                </MenuItem>
                <MenuItem
                  data-testid='spouse-employment-status-unemployed'
                  value='UNEMPLOYED'
                >
                  {t('edit.employmentStatusOptions.UNEMPLOYED')}
                </MenuItem>
                <MenuItem
                  data-testid='spouse-employment-status-stay-at-home-parent'
                  value='STAY_AT_HOME_PARENT'
                >
                  {t('edit.employmentStatusOptions.STAY_AT_HOME_PARENT')}
                </MenuItem>
                <MenuItem
                  data-testid='spouse-employment-status-student'
                  value='STUDENT'
                >
                  {t('edit.employmentStatusOptions.STUDENT')}
                </MenuItem>
                <MenuItem
                  data-testid='spouse-employment-status-retired'
                  value='RETIRED'
                >
                  {t('edit.employmentStatusOptions.RETIRED')}
                </MenuItem>
              </SelectField>
            </Grid>
          )}
        {options?.spouseCompanyType?.enabled &&
          requireSpousalInfo(userData) &&
          requireSpousalEmploymentInfo(userData) && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              {options?.spouseCompanyType?.selectField?.checked ? (
                <Autocomplete
                  testId='spouse-company-type'
                  onChange={(_: React.SyntheticEvent, value: string) =>
                    update({ ...userData, spouseCompanyType: value })
                  }
                  disabled={loading || spouseFieldLocked('spouseCompanyType')}
                  label={spouseCompanyTypeLabel}
                  infoTooltip={additionalInfo(
                    translateBackendString,
                    options?.spouseCompanyType?.additionalInfo,
                  )}
                  options={professions}
                  locked={
                    options?.spouseCompanyType?.required ===
                    FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={lockMessage()}
                  fullWidth
                  value={userData.spouseCompanyType ?? ''}
                  onBlur={() => setFocused([...focused, 'spouseCompanyType'])}
                  error={
                    !userData.spouseCompanyType &&
                    requireSpousalInfo(userData) &&
                    focused.includes('spouseCompanyType') &&
                    options?.spouseCompanyType?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  renderOption={(props: any, option: any) => (
                    <li
                      {...props}
                      data-testid={`spouse-company-type-autocomplete-${kebabCase(option)}`}
                    >
                      {option}
                    </li>
                  )}
                />
              ) : (
                <TextField
                  testId='spouse-company-type'
                  onChange={(e: any) =>
                    update({ ...userData, spouseCompanyType: e.target.value })
                  }
                  disabled={loading || spouseFieldLocked('spouseCompanyType')}
                  label={spouseCompanyTypeLabel}
                  infoTooltip={additionalInfo(
                    translateBackendString,
                    options?.spouseCompanyType?.additionalInfo,
                  )}
                  locked={
                    options?.spouseCompanyType?.required ===
                    FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={lockMessage()}
                  fullWidth
                  value={userData.spouseCompanyType ?? ''}
                  onBlur={() => setFocused([...focused, 'spouseCompanyType'])}
                  error={
                    ((!userData.spouseCompanyType &&
                      requireSpousalInfo(userData) &&
                      focused.includes('spouseCompanyType')) ||
                      invalidFieldsList.includes('spouseCompanyType')) &&
                    options?.spouseCompanyType?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                />
              )}
            </Grid>
          )}
        {options?.spouseJobTitle?.enabled &&
          requireSpousalInfo(userData) &&
          ['EMPLOYED', 'SELF_EMPLOYED'].includes(
            userData.spouseEmploymentStatus,
          ) && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              {options?.spouseJobTitle?.selectField?.checked ? (
                <Autocomplete
                  testId='spouse-job-title'
                  onChange={(_: React.SyntheticEvent, value: string) =>
                    update({ ...userData, spouseJobTitle: value })
                  }
                  disabled={loading || spouseFieldLocked('spouseJobTitle')}
                  label={translateBackendString(options?.spouseJobTitle?.label)}
                  infoTooltip={additionalInfo(
                    translateBackendString,
                    options?.spouseJobTitle?.additionalInfo,
                  )}
                  options={jobTitles}
                  locked={
                    options?.spouseJobTitle?.required ===
                    FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={lockMessage()}
                  fullWidth
                  value={userData.spouseJobTitle ?? ''}
                  onBlur={() => setFocused([...focused, 'spouseJobTitle'])}
                  error={
                    !userData.spouseJobTitle &&
                    requireSpousalInfo(userData) &&
                    focused.includes('spouseJobTitle') &&
                    options?.spouseJobTitle?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  renderOption={(props: any, option: any) => (
                    <li
                      {...props}
                      data-testid={`spouse-job-title-autocomplete-${kebabCase(option)}`}
                    >
                      {option}
                    </li>
                  )}
                />
              ) : (
                <TextField
                  testId='spouse-job-title'
                  onChange={(e: any) =>
                    update({ ...userData, spouseJobTitle: e.target.value })
                  }
                  disabled={loading || spouseFieldLocked('spouseJobTitle')}
                  label={translateBackendString(options?.spouseJobTitle?.label)}
                  infoTooltip={additionalInfo(
                    translateBackendString,
                    options?.spouseJobTitle?.additionalInfo,
                  )}
                  locked={
                    options?.spouseJobTitle?.required ===
                    FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={lockMessage()}
                  fullWidth
                  value={userData.spouseJobTitle ?? ''}
                  onBlur={() => setFocused([...focused, 'spouseJobTitle'])}
                  error={
                    ((!userData.spouseJobTitle &&
                      requireSpousalInfo(userData) &&
                      focused.includes('spouseJobTitle')) ||
                      invalidFieldsList.includes('spouseJobTitle')) &&
                    options?.spouseJobTitle?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                />
              )}
            </Grid>
          )}
        {options?.spouseAreaOfStudy?.enabled &&
          requireSpousalInfo(userData) &&
          userData.spouseEmploymentStatus === 'STUDENT' && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              {options?.spouseAreaOfStudy?.selectField?.checked ? (
                <Autocomplete
                  testId='spouse-area-of-study'
                  label={translateBackendString(
                    options?.spouseAreaOfStudy?.label,
                  )}
                  infoTooltip={additionalInfo(
                    translateBackendString,
                    options?.spouseAreaOfStudy?.additionalInfo,
                  )}
                  options={areasOfStudy}
                  value={userData.spouseAreaOfStudy ?? ''}
                  fullWidth
                  onChange={(_: React.SyntheticEvent, value: string) =>
                    update({ ...userData, spouseAreaOfStudy: value })
                  }
                  onBlur={() => setFocused([...focused, 'spouseAreaOfStudy'])}
                  disabled={
                    loading ||
                    spouseFieldLocked('spouseAreaOfStudy') ||
                    options?.spouseAreaOfStudy?.required ===
                      FieldRequiredType.NOT_EDITABLE
                  }
                  error={
                    !userData.spouseAreaOfStudy &&
                    focused.includes('spouseAreaOfStudy') &&
                    options?.spouseAreaOfStudy?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  locked={
                    options?.spouseAreaOfStudy?.required ===
                    FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={lockMessage()}
                  renderOption={(props: any, option: any) => (
                    <li
                      {...props}
                      data-testid={`student-area-of-study-${kebabCase(option)}`}
                    >
                      {option}
                    </li>
                  )}
                />
              ) : (
                <TextField
                  testId='spouse-area-of-study'
                  onChange={(e: any) =>
                    update({ ...userData, spouseAreaOfStudy: e.target.value })
                  }
                  disabled={
                    loading ||
                    spouseFieldLocked('spouseAreaOfStudy') ||
                    options?.spouseAreaOfStudy?.required ===
                      FieldRequiredType.NOT_EDITABLE
                  }
                  locked={
                    options?.spouseAreaOfStudy?.required ===
                    FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={lockMessage()}
                  label={translateBackendString(
                    options?.spouseAreaOfStudy?.label,
                  )}
                  infoTooltip={additionalInfo(
                    translateBackendString,
                    options?.spouseAreaOfStudy?.additionalInfo,
                  )}
                  fullWidth
                  value={userData.spouseAreaOfStudy}
                  onBlur={() => setFocused([...focused, 'spouseAreaOfStudy'])}
                  error={
                    ((!userData.spouseAreaOfStudy &&
                      focused.includes('spouseAreaOfStudy')) ||
                      invalidFieldsList.includes('spouseAreaOfStudy')) &&
                    options?.spouseAreaOfStudy?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                />
              )}
            </Grid>
          )}
        {options?.spouseEmployerName?.enabled &&
          requireSpousalInfo(userData) &&
          userData.spouseEmploymentStatus === 'EMPLOYED' && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              <TextField
                testId='spouse-employer-name'
                onChange={(e: any) =>
                  update({ ...userData, spouseEmployerName: e.target.value })
                }
                disabled={loading || spouseFieldLocked('spouseEmployerName')}
                label={translateBackendString(
                  options?.spouseEmployerName?.label,
                )}
                infoTooltip={additionalInfo(
                  translateBackendString,
                  options?.spouseEmployerName?.additionalInfo,
                )}
                locked={
                  options?.spouseEmployerName?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                lockMessage={lockMessage()}
                fullWidth
                value={userData.spouseEmployerName ?? ''}
                onBlur={() => setFocused([...focused, 'spouseEmployerName'])}
                error={
                  ((!userData.spouseEmployerName &&
                    requireSpousalInfo(userData) &&
                    focused.includes('spouseEmployerName')) ||
                    invalidFieldsList.includes('spouseEmployerName')) &&
                  options?.spouseEmployerName?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
              />
            </Grid>
          )}
        {options?.spouseEmployerCompanyAddress?.enabled &&
          requireSpousalInfo(userData) &&
          userData.spouseEmploymentStatus === 'EMPLOYED' && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              <AddressField
                testId='spouse-employer-company-address'
                onChange={(e: PhysicalAddress) =>
                  update({ ...userData, spouseEmployerCompanyAddress: e })
                }
                disabled={
                  loading || spouseFieldLocked('spouseEmployerCompanyAddress')
                }
                locked={
                  options?.spouseEmployerCompanyAddress?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                label={translateBackendString(
                  options?.spouseEmployerCompanyAddress?.label,
                )}
                infoTooltip={additionalInfo(
                  translateBackendString,
                  options?.spouseEmployerCompanyAddress?.additionalInfo,
                )}
                lockMessage={lockMessage()}
                fullWidth
                address={
                  userData.spouseEmployerCompanyAddress as PhysicalAddress
                }
                onFocus={() =>
                  setFocused([...focused, 'spouseEmployerCompanyAddress'])
                }
                error={
                  ((!userData.spouseEmployerCompanyAddress?.streetName &&
                    focused.includes('spouseEmployerCompanyAddress')) ||
                    invalidFieldsList.includes(
                      'spouseEmployerCompanyAddress',
                    )) &&
                  focused.includes('spouseEmployerCompanyAddress') &&
                  options?.spouseEmployerCompanyAddress?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
              />
            </Grid>
          )}
        {options?.spouseEmployerCompanyPhone?.enabled &&
          requireSpousalInfo(userData) &&
          userData.spouseEmploymentStatus === 'EMPLOYED' && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              <PhoneNumberField
                testId='spouse-employer-company-phone'
                onChange={(e: any) => {
                  update({ ...userData, spouseEmployerCompanyPhone: e.value });
                  setCountryCode({
                    ...countryCode,
                    spouseEmployerCompanyPhone: e.countryCode,
                  });
                }}
                disabled={
                  loading ||
                  options?.spouseEmployerCompanyPhone?.required ===
                    FieldRequiredType.NOT_EDITABLE ||
                  spousePhoneLocked('spouseEmployerCompanyPhone')
                }
                label={translateBackendString(
                  options?.spouseEmployerCompanyPhone?.label,
                )}
                fullWidth
                value={userData.spouseEmployerCompanyPhone ?? ''}
                onBlur={() =>
                  setFocused([...focused, 'spouseEmployerCompanyPhone'])
                }
                error={
                  ((!userData.spouseEmployerCompanyPhone &&
                    focused.includes('spouseEmployerCompanyPhone')) ||
                    invalidFieldsList.includes('spouseEmployerCompanyPhone')) &&
                  (options?.spouseEmployerCompanyPhone?.required !==
                    FieldRequiredType.NOT_REQUIRED ||
                    invalidPhone.spouseEmployerCompanyPhone)
                }
                errorText={
                  invalidPhone.spouseEmployerCompanyPhone
                    ? t('components:fieldErrors.phone')
                    : undefined
                }
                locked={
                  options?.spouseEmployerCompanyPhone?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                lockMessage={lockMessage()}
                infoTooltip={additionalInfo(
                  translateBackendString,
                  options?.spouseEmployerCompanyPhone?.additionalInfo,
                )}
              />
            </Grid>
          )}
        {options.spouseAnnualIncome?.enabled &&
          requireSpousalInfo(userData) && (
            <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
              {options?.spouseAnnualIncome?.selectField?.checked ? (
                <SelectField
                  disabled={
                    loading || spouseFieldLocked('spouseAnnualIncomeStr')
                  }
                  label={translateBackendString(
                    options?.spouseAnnualIncome?.label,
                  )}
                  value={userData.spouseAnnualIncomeStr ?? ''}
                  infoTooltip={additionalInfo(
                    translateBackendString,
                    options?.spouseAnnualIncome?.additionalInfo,
                  )}
                  fullWidth
                  locked={
                    options?.spouseAnnualIncome?.required ===
                    FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={lockMessage()}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const selectedValue = e.target.value;
                    update({
                      ...userData,
                      spouseAnnualIncomeStr: selectedValue,
                    });
                  }}
                  onBlur={() => setFocused([...focused, 'spouseAnnualIncome'])}
                  error={
                    ((!userData.spouseAnnualIncomeStr &&
                      requireSpousalInfo(userData) &&
                      focused.includes('spouseAnnualIncome')) ||
                      invalidFieldsList.includes('spouseAnnualIncomeStr')) &&
                    options?.spouseAnnualIncome?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  testId='spouse-annual-income-select'
                >
                  {options?.spouseAnnualIncome?.selectField?.options.map(
                    (selectOption: SelectOption) => (
                      <MenuItem
                        data-testid={`spouse-annual-income-${kebabCase(selectOption.value)}`}
                        value={selectOption.value}
                      >
                        {selectOption.label}
                      </MenuItem>
                    ),
                  )}
                </SelectField>
              ) : (
                <TextField
                  type='dollar'
                  value={cents2dollars(userData.spouseAnnualIncomeCents)}
                  onChange={(e: any) =>
                    update({
                      ...userData,
                      spouseAnnualIncomeCents: dollars2cents(e.target.value),
                    })
                  }
                  disabled={
                    loading || spouseFieldLocked('spouseAnnualIncomeCents')
                  }
                  locked={
                    options?.spouseAnnualIncome?.required ===
                    FieldRequiredType.NOT_EDITABLE
                  }
                  lockMessage={lockMessage()}
                  label={translateBackendString(
                    options?.spouseAnnualIncome?.label,
                  )}
                  infoTooltip={additionalInfo(
                    translateBackendString,
                    options?.spouseAnnualIncome?.additionalInfo,
                  )}
                  fullWidth
                  leadingIcon='dollar'
                  onBlur={() => setFocused([...focused, 'spouseAnnualIncome'])}
                  error={
                    ((userData.spouseAnnualIncomeCents === undefined &&
                      requireSpousalInfo(userData) &&
                      focused.includes('spouseAnnualIncome')) ||
                      invalidFieldsList.includes('spouseAnnualIncomeCents')) &&
                    options?.spouseAnnualIncome?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  testId='spouse-annual-income'
                />
              )}
            </Grid>
          )}
        {anyTrustedContactFieldEnabled && relationOptions.length > 0 && (
          <Grid size={12}>
            <Grid
              container
              spacing={2}
              sx={{
                alignItems: 'flex-end',
              }}
            >
              <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
                <SelectField
                  testId='trusted-contact-select'
                  label={t(
                    'shared:relationshipInformation.selectTrustedContact',
                  )}
                  fullWidth
                  disabled={loading}
                  value={selectedTcpId}
                  onChange={async (e: any) => {
                    const relationUser = relationOptions.find(
                      (r: any) => r.id === e.target.value,
                    );
                    if (!relationUser) return;
                    // Snapshot so we can roll back if persistence fails.
                    const previousTcpId = selectedTcpId;
                    const previousUserData = userData;
                    setSelectedTcpId(e.target.value);
                    applyTrustedContactFromUser(relationUser);
                    try {
                      await onSelectRelationEntity?.(
                        relationUser.id,
                        RelatedEntityTypes.TrustedContact,
                      );
                    } catch {
                      setSelectedTcpId(previousTcpId);
                      update(previousUserData);
                    }
                  }}
                >
                  {relationOptions.map((relationUser: any) => (
                    <MenuItem
                      key={relationUser.id}
                      value={relationUser.id}
                      data-testid={`trusted-contact-option-${relationUser.id}`}
                    >
                      {relationOptionLabel(relationUser)}
                    </MenuItem>
                  ))}
                </SelectField>
              </Grid>
              {trustedContactAutoFilled && (
                <Grid size='auto'>
                  <Button
                    variant='outlined'
                    size='small'
                    data-testid='trusted-contact-select-clear'
                    disabled={loading}
                    onClick={clearSelectedTrustedContact}
                  >
                    {t(
                      'shared:relationshipInformation.clearSelectedTrustedContact',
                    )}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
        {options?.trustedContactName?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='trusted-contact-name'
              onChange={(e: any) =>
                update({
                  ...userData,
                  trustedContactPerson: {
                    ...userData.trustedContactPerson,
                    name: e.target.value,
                  },
                })
              }
              disabled={loading || trustedContactFieldLocked('name')}
              label={translateBackendString(options?.trustedContactName?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.trustedContactName?.additionalInfo,
              )}
              locked={
                options?.trustedContactName?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              fullWidth
              value={userData.trustedContactPerson?.name ?? ''}
              onBlur={() => setFocused([...focused, 'trustedContactName'])}
              error={
                ((!userData.trustedContactPerson?.name &&
                  focused.includes('trustedContactName')) ||
                  invalidFieldsList.includes('trustedContactName')) &&
                options?.trustedContactName?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.trustedContactEmail?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='trusted-contact-email'
              onChange={(e: any) =>
                update({
                  ...userData,
                  trustedContactPerson: {
                    ...userData.trustedContactPerson,
                    email: e.target.value,
                  },
                })
              }
              disabled={loading || trustedContactFieldLocked('email')}
              label={translateBackendString(
                options?.trustedContactEmail?.label,
              )}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.trustedContactEmail?.additionalInfo,
              )}
              locked={
                options?.trustedContactEmail?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              fullWidth
              value={userData.trustedContactPerson?.email ?? ''}
              onBlur={() => setFocused([...focused, 'trustedContactEmail'])}
              error={
                ((!userData.trustedContactPerson?.email &&
                  focused.includes('trustedContactEmail')) ||
                  invalidFieldsList.includes('trustedContactEmail')) &&
                options?.trustedContactEmail?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.trustedContactPhone?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <PhoneNumberField
              testId='trusted-contact-phone'
              onChange={(e: any) => {
                update({
                  ...userData,
                  trustedContactPerson: {
                    ...userData.trustedContactPerson,
                    phone: e.value,
                  },
                });
                setCountryCode({
                  ...countryCode,
                  trustedContactPhone: e.countryCode,
                });
              }}
              disabled={loading || trustedContactPhoneLocked('phone')}
              label={translateBackendString(
                options?.trustedContactPhone?.label,
              )}
              locked={
                options?.trustedContactPhone?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              fullWidth
              value={userData.trustedContactPerson?.phone ?? ''}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.trustedContactPhone?.additionalInfo,
              )}
              onBlur={() => setFocused([...focused, 'trustedContactPhone'])}
              errorText={
                invalidPhone.trustedContactPhone
                  ? t('components:fieldErrors.phone')
                  : undefined
              }
              error={
                ((!userData.trustedContactPerson?.phone &&
                  focused.includes('trustedContactPhone')) ||
                  invalidFieldsList.includes('trustedContactPhone')) &&
                focused.includes('trustedContactPhone') &&
                (options?.trustedContactPhone?.required !==
                  FieldRequiredType.NOT_REQUIRED ||
                  invalidPhone.trustedContactPhone)
              }
            />
          </Grid>
        )}
        {options?.trustedContactAddress?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <AddressField
              testId='trusted-contact-address'
              onChange={(e) =>
                update({
                  ...userData,
                  trustedContactPerson: {
                    ...userData.trustedContactPerson,
                    physicalAddress: e,
                  },
                })
              }
              disabled={loading || trustedContactFieldLocked('physicalAddress')}
              label={translateBackendString(
                options?.trustedContactAddress?.label,
              )}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.trustedContactAddress?.additionalInfo,
              )}
              locked={
                options?.trustedContactAddress?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              fullWidth
              address={
                userData.trustedContactPerson
                  ?.physicalAddress as PhysicalAddress
              }
              onFocus={() => setFocused([...focused, 'trustedContactAddress'])}
              error={
                ((!userData.trustedContactPerson?.physicalAddress?.streetName &&
                  focused.includes('trustedContactAddress')) ||
                  invalidFieldsList.includes('trustedContactAddress')) &&
                focused.includes('trustedContactAddress') &&
                options?.trustedContactAddress?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.trustedContactRelation?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <SelectField
              testId='trusted-contact-relation'
              onChange={(e: any) =>
                update({
                  ...userData,
                  trustedContactPerson: {
                    ...userData.trustedContactPerson,
                    relation: e.target.value,
                  },
                })
              }
              disabled={loading}
              label={translateBackendString(
                options?.trustedContactRelation?.label,
              )}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.trustedContactRelation?.additionalInfo,
              )}
              locked={
                options?.trustedContactRelation?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              fullWidth
              value={userData.trustedContactPerson?.relation ?? ''}
              onBlur={() => setFocused([...focused, 'trustedContactRelation'])}
              error={
                ((!userData.trustedContactPerson?.relation &&
                  focused.includes('trustedContactRelation')) ||
                  invalidFieldsList.includes('trustedContactRelation')) &&
                options?.trustedContactRelation?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            >
              <MenuItem
                data-testid='trusted-contact-relation-spouse'
                value='SPOUSE'
              >
                {t('edit.relationOptions.SPOUSE')}
              </MenuItem>
              <MenuItem
                data-testid='trusted-contact-relation-parent'
                value='PARENT'
              >
                {t('edit.relationOptions.PARENT')}
              </MenuItem>
              <MenuItem
                data-testid='trusted-contact-relation-sibling'
                value='SIBLING'
              >
                {t('edit.relationOptions.SIBLING')}
              </MenuItem>
              <MenuItem
                data-testid='trusted-contact-relation-child'
                value='CHILD'
              >
                {t('edit.relationOptions.CHILD')}
              </MenuItem>
              <MenuItem
                data-testid='trusted-contact-relation-grandparent'
                value='GRANDPARENT'
              >
                {t('edit.relationOptions.GRANDPARENT')}
              </MenuItem>
              <MenuItem
                data-testid='trusted-contact-relation-friend'
                value='FRIEND'
              >
                {t('edit.relationOptions.FRIEND')}
              </MenuItem>
              <MenuItem
                data-testid='trusted-contact-relation-other'
                value='OTHER'
              >
                {t('edit.relationOptions.OTHER')}
              </MenuItem>
            </SelectField>
          </Grid>
        )}
        {options?.numberOfDependents?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <NumberField
              testId='number-of-dependents'
              setNumber={(number) =>
                update({
                  ...userData,
                  numberOfDependents: parseInt(number),
                })
              }
              min={0}
              disabled={loading}
              label={translateBackendString(options?.numberOfDependents?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.numberOfDependents?.additionalInfo,
              )}
              number={userData.numberOfDependents ?? 0}
              onBlur={() => setFocused([...focused, 'numberOfDependents'])}
              error={
                ((userData.numberOfDependents == null &&
                  focused.includes('numberOfDependents')) ||
                  invalidFieldsList.includes('numberOfDependents')) &&
                options?.numberOfDependents?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
              lockMessage={lockMessage()}
              locked={
                options?.numberOfDependents?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
            />
          </Grid>
        )}
        <Grid size={12}>
          {renderCustomFields({
            customData: userData,
            update,
            grid,
            focused,
            setFocused,
            loading,
            ignoreCustomFieldTriggerRules,
            userId: userData.id,
          })}
        </Grid>
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default RelationshipInformationVisual;
